import React, { useContext, useState } from 'react';
import { Flex } from 'antd';

import IBTooltip from '@totem/components/common/helpTooltips/IBTooltip';
import { EMPTY_ID } from '@totem/components/common/reference/ReferenceSelector';
import AttributesTable from '@totem/components/devices/deviceDetails/AttributesTable';
import DeviceContext from '@totem/components/devices/deviceDetails/DeviceContext';
import DeviceKeysTable from '@totem/components/devices/deviceDetails/deviceKeys/DeviceKeysTable';
import DeviceVariablesTable from '@totem/components/devices/deviceDetails/deviceVariables/DeviceVariablesTable';
import { hasVariables } from '@totem/components/devices/deviceDetails/deviceVariables/utilities';
import DriveCards from '@totem/components/devices/deviceDetails/DriveCards';
import EventsTable from '@totem/components/devices/deviceDetails/EventsTable';
import MaintenanceWindows from '@totem/components/devices/deviceDetails/maintenanceWindows';
import MemorySlotsTable from '@totem/components/devices/deviceDetails/MemorySlotTable';
import NetworkDetails from '@totem/components/devices/deviceDetails/NetworkDetails';
import NetworkLinksContainer from '@totem/components/devices/deviceDetails/networkLinks/networkLinksContainer';
import PeripheralsTable from '@totem/components/devices/deviceDetails/PeripheralsTable';
import SalesOrderLinesTable from '@totem/components/devices/deviceDetails/SalesOrderLinesTable';
import VulnerabilityReportTable from '@totem/components/devices/deviceDetails/softwareVulnerabilityTable';
import SourcedAttributesTable from '@totem/components/devices/deviceDetails/SourcedAttributesTable';
import VideoCardsTable from '@totem/components/devices/deviceDetails/VideoCardTable';
import NetworkContainer from '@totem/components/devices/network/networkContainer';
import NetworkTable from '@totem/components/devices/network/networkTable';
import { hasNetworkInterfaces } from '@totem/components/devices/network/utilities';
import OSPatchInstallsContainer from '@totem/components/devices/os-patch-installs/osPatchInstallContainer';
import OSPatchInstallsTable from '@totem/components/devices/os-patch-installs/osPatchInstallTable';
import { hasOSPatchInstalls } from '@totem/components/devices/os-patch-installs/utilities';
import OSPatchContainer from '@totem/components/devices/os-patches/osPatchContainer';
import OSPatchTable from '@totem/components/devices/os-patches/osPatchTable';
import { hasOSPatches } from '@totem/components/devices/os-patches/utilities';
import ProcessorsContainer from '@totem/components/devices/processors/processorsContainer';
import ProcessorsTable from '@totem/components/devices/processors/processorsTable';
import { hasProcessors } from '@totem/components/devices/processors/utilities';
import ServiceContainer from '@totem/components/devices/services/serviceContainer';
import ServicesTable from '@totem/components/devices/services/servicesTable';
import { hasServices } from '@totem/components/devices/services/utilities';
import SoftwareContainer from '@totem/components/devices/software/softwareContainer';
import SoftwareTable from '@totem/components/devices/software/softwareTable';
import { hasSoftware } from '@totem/components/devices/software/utilities';
import SoftwarePatchContainer from '@totem/components/devices/software-patches/softwarePatchesContainer';
import SoftwarePatchesTable from '@totem/components/devices/software-patches/softwarePatchesTable';
import { hasSoftwarePatches } from '@totem/components/devices/software-patches/utilities';
import { hasVolumes } from '@totem/components/devices/volumes/utilities';
import VolumeCards from '@totem/components/devices/volumes/volumeCards';
import VolumesContainer from '@totem/components/devices/volumes/volumeContainer';
import VolumeTable from '@totem/components/devices/volumes/volumeTable';
import DocumentsContainer from '@totem/components/documents/report/documentsContainer';
import DocumentsPanel from '@totem/components/documents/report/documentsPanel';
import NetworkGraph from '@totem/components/portfolio/buildings/details/network/networkGraph';
import TicketList from '@totem/components/services/threats/detailView/TicketList';
import VulnerabilityReportContext from '@totem/components/software/vulnerabilityReport/vulnerabilityReportContext';
import SupportLineAddButton from '@totem/components/supportLines/supportLineAddButton';
import SupportLinesContainer from '@totem/components/supportLines/supportLinesContainer';
import SupportLinesRecordCount from '@totem/components/supportLines/supportLinesRecordCount';
import SupportLinesTable from '@totem/components/supportLines/supportLinesTable';
import TicketListContainer, {
  containerConfiguration as ticketContainerConfiguration,
} from '@totem/components/ticketing/listContainer/TicketListContainer';
import IBTabs, {
  IBTabItem,
} from '@totem/components/ui_components/antDesignOverrides/IBTabs';
import HeaderItem from '@totem/components/ui_components/headers/HeaderItem';
import InputContainer from '@totem/components/ui_components/reportSettings/InputContainer';
import { permissions } from '@totem/components/userContext/security/permissions/permissions';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import VulnerabilityReportContainer from '@totem/components/vulnerabilities/report/vulnerabilityReportContainer';
import DeviceVulnerabilityReportTable from '@totem/components/vulnerabilities/report/vulnerabilityReportTable';
import colors from '@totem/styles/colors';
import { SecurityGroupCheckConstraints } from '@totem/types/user';
import { isNotNull } from '@totem/utilities/common';

const COLOR_HARDWARE = '#5188bb';
const COLOR_SOFTWARE = '#0d629a';
const COLOR_SUPPORT = '#0d3157';
const COLOR_SELECTED = colors.branding.accent;

const styles = {
  inset: {
    marginLeft: '20px',
    marginRight: '20px',
  },
};

const showSoftwarePatching = false;

const DeviceTabs = () => {
  const { deviceData, deviceId } = useContext(DeviceContext);
  const { userProfile } = useContext(UserProfileContext);
  const { data } = useContext(VulnerabilityReportContext);

  const [activeTab, setActiveTab] = useState<string>('attributes');

  const hasAdvancedManagement =
    permissions.advancedManagement.hasPermissions(userProfile);

  const hasNozomiKey =
    isNotNull(deviceData) && isNotNull(deviceData.device.keys)
      ? deviceData.device.keys.findIndex(
          (chk) => chk.type === 'NOZOMI_VANTAGE_ID',
        ) >= 0
      : false;

  const securityCheckConstraints: SecurityGroupCheckConstraints = {
    regionId:
      typeof deviceData !== 'undefined' && deviceData !== null
        ? deviceData.device.regionId
        : EMPTY_ID,
    buildingId:
      typeof deviceData !== 'undefined' && deviceData !== null
        ? deviceData.device.buildingId
        : EMPTY_ID,
    controlSystemId:
      typeof deviceData !== 'undefined' && deviceData !== null
        ? deviceData.device.controlSystemId
        : EMPTY_ID,
  };

  const getTabItems = () => {
    const tabItems: IBTabItem[] = [];

    tabItems.push({
      key: 'attributes',
      label: (
        <span
          style={{
            color: activeTab === 'attributes' ? COLOR_SELECTED : COLOR_HARDWARE,
          }}
        >
          Attributes
        </span>
      ),
      group: 'Hardware',
      children: (
        <>
          <AttributesTable />
          {permissions.internal.hasPermissions(userProfile) &&
            isNotNull(deviceData.device.sourcedAttributes) &&
            deviceData.device.sourcedAttributes.length > 0 && (
              <>
                <br />
                <br />
                <SourcedAttributesTable />
              </>
            )}
        </>
      ),
    });

    if (hasProcessors(deviceData)) {
      tabItems.push({
        key: 'processors',
        label: (
          <span
            style={{
              color:
                activeTab === 'processors' ? COLOR_SELECTED : COLOR_HARDWARE,
            }}
          >
            Processors
          </span>
        ),
        group: 'Hardware',
        children: (
          <ProcessorsContainer id={deviceData.device.id}>
            <ProcessorsTable />
          </ProcessorsContainer>
        ),
      });
    }

    if (hasVolumes(deviceData)) {
      tabItems.push({
        key: 'drives',
        label: (
          <span
            style={{
              color: activeTab === 'drives' ? COLOR_SELECTED : COLOR_HARDWARE,
            }}
          >
            Volumes
          </span>
        ),
        group: 'Hardware',
        children: (
          <VolumesContainer id={deviceData.device.id}>
            <VolumeCards />
            <br />
            <VolumeTable />
          </VolumesContainer>
        ),
      });
    }

    if (
      !hasVolumes(deviceData) &&
      hasAdvancedManagement &&
      deviceData.device.drives !== null &&
      deviceData.device.drives.length > 0
    ) {
      tabItems.push({
        key: 'drives',
        label: (
          <IBTooltip content={permissions.advancedManagement.tooltip}>
            <span
              style={{
                color: activeTab === 'drives' ? COLOR_SELECTED : COLOR_HARDWARE,
              }}
            >
              Volumes
            </span>
          </IBTooltip>
        ),
        group: 'Hardware',
        children: <DriveCards />,
      });
    }

    if (hasServices(deviceData)) {
      tabItems.push({
        key: 'services',
        label: (
          <span
            style={{
              color: activeTab === 'services' ? COLOR_SELECTED : COLOR_HARDWARE,
            }}
          >
            Services
          </span>
        ),
        group: 'Hardware',
        children: (
          <ServiceContainer id={deviceData.device.id}>
            <ServicesTable />
          </ServiceContainer>
        ),
      });
    }

    if (
      hasAdvancedManagement &&
      deviceData.device.memorySlots !== null &&
      deviceData.device.memorySlots.length > 0
    ) {
      tabItems.push({
        key: 'memory',
        label: (
          <IBTooltip content={permissions.advancedManagement.tooltip}>
            <span
              style={{
                color: activeTab === 'memory' ? COLOR_SELECTED : COLOR_HARDWARE,
              }}
            >
              Memory
            </span>
          </IBTooltip>
        ),
        group: 'Hardware',
        children: <MemorySlotsTable />,
      });
    }

    tabItems.push({
      key: 'network',
      label: (
        <span
          style={{
            color: activeTab === 'network' ? COLOR_SELECTED : COLOR_HARDWARE,
          }}
        >
          Network
        </span>
      ),
      group: 'Hardware',
      children: (
        <>
          <NetworkDetails />
          {hasNetworkInterfaces(deviceData) && (
            <NetworkContainer id={deviceData.device.id}>
              <br />
              <br />
              <NetworkTable />
            </NetworkContainer>
          )}
        </>
      ),
    });

    if (hasNozomiKey) {
      tabItems.push({
        key: 'network_links',
        label: (
          <span
            style={{
              color:
                activeTab === 'network_links' ? COLOR_SELECTED : COLOR_HARDWARE,
            }}
          >
            Links
          </span>
        ),
        group: 'Hardware',
        children: (
          <>
            <NetworkLinksContainer deviceId={deviceData.device.id}>
              <NetworkGraph />
            </NetworkLinksContainer>
          </>
        ),
      });
    }

    if (
      hasAdvancedManagement &&
      deviceData.device.videoCards !== null &&
      deviceData.device.videoCards.length > 0
    ) {
      tabItems.push({
        key: 'video',
        label: (
          <IBTooltip content={permissions.advancedManagement.tooltip}>
            <span
              style={{
                color: activeTab === 'video' ? COLOR_SELECTED : COLOR_HARDWARE,
              }}
            >
              Video
            </span>
          </IBTooltip>
        ),
        group: 'Hardware',
        children: <VideoCardsTable />,
      });
    }

    if (
      hasAdvancedManagement &&
      deviceData.device.peripherals !== null &&
      deviceData.device.peripherals.length > 0
    ) {
      tabItems.push({
        key: 'peripherals',
        label: (
          <IBTooltip content={permissions.advancedManagement.tooltip}>
            <span
              style={{
                color:
                  activeTab === 'peripherals' ? COLOR_SELECTED : COLOR_HARDWARE,
              }}
            >
              Peripherals
            </span>
          </IBTooltip>
        ),
        group: 'Hardware',
        children: <PeripheralsTable />,
      });
    }

    if (hasSoftware(deviceData)) {
      tabItems.push({
        key: 'software',
        label: (
          <span
            style={{
              color: activeTab === 'software' ? COLOR_SELECTED : COLOR_SOFTWARE,
            }}
          >
            Software
          </span>
        ),
        group: 'Software',
        children: (
          <SoftwareContainer id={deviceData.device.id}>
            <SoftwareTable />,
          </SoftwareContainer>
        ),
      });
    }

    if (hasOSPatchInstalls(deviceData)) {
      tabItems.push({
        key: 'os-patch-installs',
        label: (
          <span
            style={{
              color:
                activeTab === 'os-patch-installs'
                  ? COLOR_SELECTED
                  : COLOR_SOFTWARE,
            }}
          >
            OS Patch Installs
          </span>
        ),
        group: 'Software',
        children: (
          <OSPatchInstallsContainer id={deviceData.device.id}>
            <OSPatchInstallsTable />
          </OSPatchInstallsContainer>
        ),
      });
    }

    if (hasOSPatches(deviceData) || hasSoftwarePatches(deviceData)) {
      tabItems.push({
        key: 'os-patches',
        label: (
          <span
            style={{
              color:
                activeTab === 'os-patches' ? COLOR_SELECTED : COLOR_SOFTWARE,
            }}
          >
            Patches
          </span>
        ),
        group: 'Software',
        children: (
          <>
            {hasOSPatches(deviceData) && (
              <>
                <HeaderItem title="OS Patches">{null}</HeaderItem>
                <OSPatchContainer id={deviceData.device.id}>
                  <OSPatchTable />
                </OSPatchContainer>
                <br />
                <br />
              </>
            )}
            {showSoftwarePatching && hasSoftwarePatches(deviceData) && (
              <>
                <HeaderItem title="Software Patches">{null}</HeaderItem>
                <SoftwarePatchContainer id={deviceData.device.id}>
                  <SoftwarePatchesTable />
                </SoftwarePatchContainer>
              </>
            )}
          </>
        ),
      });
    }

    if (
      hasSoftware(deviceData) &&
      isNotNull(data) &&
      isNotNull(data.data) &&
      data.data.length > 0
    ) {
      tabItems.push({
        key: 'software_vulnerabilities',
        label: (
          <span
            style={{
              color:
                activeTab === 'software_vulnerabilities'
                  ? COLOR_SELECTED
                  : COLOR_SOFTWARE,
            }}
          >
            Software Vulnerabilities
          </span>
        ),
        group: 'Software',
        children: (
          <VulnerabilityReportTable
            deviceId={deviceData.device.id}
            deviceName={deviceData.device.displayName}
          />
        ),
      });
    }

    if (deviceData.device.flags.monitored.status === 'ACTIVE') {
      tabItems.push({
        key: 'device_vulnerabilities',
        label: (
          <span
            style={{
              color:
                activeTab === 'device_vulnerabilities'
                  ? COLOR_SELECTED
                  : COLOR_SOFTWARE,
            }}
          >
            Device Vulnerabilities
          </span>
        ),
        group: 'Software',
        children: (
          <VulnerabilityReportContainer
            staticFilters={{ ownerId: [deviceData.device.id] }}
          >
            <DeviceVulnerabilityReportTable />
          </VulnerabilityReportContainer>
        ),
      });
    }

    tabItems.push({
      key: 'events',
      label: (
        <span
          style={{
            color: activeTab === 'events' ? COLOR_SELECTED : COLOR_SUPPORT,
          }}
        >
          Events
        </span>
      ),
      group: 'Support',
      children: <EventsTable />,
    });

    if (
      permissions.ticketingViewer.hasPermissions(
        userProfile,
        securityCheckConstraints,
      )
    ) {
      tabItems.push({
        key: 'tickets',
        label: (
          <IBTooltip content={permissions.ticketingViewer.tooltip}>
            <span
              style={{
                color: activeTab === 'tickets' ? COLOR_SELECTED : COLOR_SUPPORT,
              }}
            >
              Tickets
            </span>
          </IBTooltip>
        ),
        group: 'Support',
        children: (
          <InputContainer
            filterConfig={ticketContainerConfiguration}
            defaultSort={{
              field: 'createdAt',
              direction: 'descend',
            }}
            staticFilters={[
              {
                field: 'deviceId',
                values: [deviceId],
              },
            ]}
            defaultFilters={[
              {
                field: 'status',
                values: ['OPEN', 'RESOLVED'],
              },
            ]}
            componentKey={`device-tickets-${deviceData.device.id}`}
          >
            <TicketListContainer>
              <TicketList />
            </TicketListContainer>
          </InputContainer>
        ),
      });
    }

    if (
      isNotNull(deviceData) &&
      isNotNull(deviceData.device) &&
      isNotNull(deviceData.device.maintenanceWindows) &&
      deviceData.device.maintenanceWindows.length > 0
    ) {
      tabItems.push({
        key: 'maintenance-windows',
        label: (
          <span
            style={{
              color:
                activeTab === 'maintenance-windows'
                  ? COLOR_SELECTED
                  : COLOR_SUPPORT,
            }}
          >
            Maintenance Windows
          </span>
        ),
        group: 'Support',
        children: <MaintenanceWindows />,
      });
    }

    if (
      permissions.contractsViewer.hasPermissions(
        userProfile,
        securityCheckConstraints,
      ) &&
      deviceData.device.salesOrders !== null &&
      deviceData.device.salesOrders.length > 0
    ) {
      tabItems.push({
        key: 'salesOrderLines',
        label: (
          <IBTooltip content={permissions.contractsViewer.tooltip}>
            <span
              style={{
                color:
                  activeTab === 'salesOrderLines'
                    ? COLOR_SELECTED
                    : COLOR_SUPPORT,
              }}
            >
              Sales Order Lines
            </span>
          </IBTooltip>
        ),
        group: 'Support',
        children: <SalesOrderLinesTable />,
      });
    }

    if (hasVariables(deviceData)) {
      tabItems.push({
        key: 'variables',
        label: (
          <span
            style={{
              color: activeTab === 'variables' ? COLOR_SELECTED : COLOR_SUPPORT,
            }}
          >
            Variables
          </span>
        ),
        group: 'Support',
        children: <DeviceVariablesTable />,
      });
    }

    tabItems.push({
      key: 'thirdPartySupport',
      label: (
        <span
          style={{
            color: activeTab === 'contract' ? COLOR_SELECTED : COLOR_SUPPORT,
          }}
        >
          Third Party Support
        </span>
      ),
      group: 'Support',
      children: (
        <SupportLinesContainer
          owner={{
            id: EMPTY_ID,
            organizationId: userProfile.organization.id,
            regionId: deviceData.device.regionId,
            buildingId: deviceData.device.buildingId,
            controlSystemId: deviceData.device.controlSystemId,
            objectType: 'Device',
            objectId: deviceData.device.id,
            objectName: deviceData.device.displayName,
            supportLines: [],
          }}
        >
          <div>
            <div style={styles.inset}>
              <Flex justify={'flex-end'}>
                <SupportLinesRecordCount />
                &nbsp;&nbsp;&nbsp;
                <SupportLineAddButton />
              </Flex>
              <br />
              <SupportLinesTable />
            </div>
          </div>
        </SupportLinesContainer>
      ),
    });

    if (isNotNull(deviceData) && isNotNull(deviceData.device)) {
      tabItems.push({
        key: 'documents',
        label: 'Documents',
        group: 'Informational',
        children: (
          <DocumentsContainer
            documentReference={{
              regionId: deviceData.region.id,
              buildingId: deviceData.building.id,
              controlSystemId: deviceData.controlSystem.id,
              objectReference: {
                type: 'Device',
                id: deviceData.device.id,
                name: deviceData.device.displayName,
                description: '',
                createdAt: '',
                createdBy: '',
              },
            }}
          >
            <div>
              <DocumentsPanel />
            </div>
          </DocumentsContainer>
        ),
      });
    }

    if (
      permissions.internalSystemAdministrator.hasPermissions(
        userProfile,
        securityCheckConstraints,
      )
    ) {
      tabItems.push({
        key: 'keys',
        label: (
          <IBTooltip content={permissions.internalSystemAdministrator.tooltip}>
            <span
              style={{
                color: activeTab === 'keys' ? COLOR_SELECTED : COLOR_SUPPORT,
              }}
            >
              Keys
            </span>
          </IBTooltip>
        ),
        group: 'Informational',
        children: <DeviceKeysTable />,
      });
    }

    return tabItems;
  };

  return (
    <>
      {typeof deviceData !== 'undefined' && deviceData !== null && (
        <IBTabs
          defaultActiveKey="attributes"
          items={getTabItems()}
          activeKey={activeTab}
          onChange={setActiveTab}
        />
      )}
    </>
  );
};

export default DeviceTabs;
